import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import {
  TEMPLATE_TYPE,
  useLoadTemplate,
  useTemplateOptions,
} from '#/entities/template'
import {
  insertFieldsToSlashGroups,
  relabelTemplateBody,
  TemplateComposeToolbar,
  useTemplateVariableOptions,
} from '#/entities/template-variable'
import { cn } from '#/shared/lib/utils'
import { useWorkspaceTabState } from '#/shared/lib/workspace-tabs'
import { Button } from '#/shared/ui/button'
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogTitle,
} from '#/shared/ui/dialog'
import { ChevronDownIcon, CloseIcon, SendIcon } from '#/shared/ui/icons'
import { Input } from '#/shared/ui/input'
import {
  buildMassRecipientsLabels,
  MassRecipients,
} from '#/shared/ui/mass-recipients'
import { NoteEditor } from '#/shared/ui/note-editor'
import type { SlashCommandGroup } from '#/shared/ui/note-editor'
import { Popover } from '#/shared/ui/popover'

import type { MassEmailViewModel } from '../lib/mass-email'
import { useMassEmail } from '../model/use-mass-email'

interface MassEmailDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onSent?: () => void
  viewModel: MassEmailViewModel
  /**
   * Keep the typed subject/body per workspace tab, so a tab switch that
   * closes the dialog brings the draft back with it. The global one-off
   * composer opts out: it is tab-independent and stays mounted across
   * switches, so its draft must not swap under the open editor.
   */
  perTabDraft?: boolean
}

type DraftState = [string, (value: string) => void]

function Header() {
  const { t } = useTranslation()
  return (
    <header className="flex shrink-0 items-center justify-between gap-2.5 border-b border-gray-200 px-5 py-2.5">
      <DialogTitle className="text-base font-semibold text-gray-500">
        {t('massEmail.title')}
      </DialogTitle>
      <DialogClose asChild>
        <button
          type="button"
          aria-label={t('massEmail.close')}
          className="flex size-8 cursor-pointer items-center justify-center rounded-md text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-900"
        >
          <CloseIcon className="size-3.5" />
        </button>
      </DialogClose>
    </header>
  )
}

function FromRow({
  fromEmails,
  value,
  onChange,
}: {
  fromEmails: string[]
  value: string
  onChange: (value: string) => void
}) {
  const { t } = useTranslation()
  return (
    <div className="flex h-9 shrink-0 items-center gap-2.5 border-b border-gray-200 px-2.5">
      <span className="text-xs text-gray-500 uppercase select-none">
        {t('massEmail.from')}
      </span>
      <Popover
        align="left"
        portal
        trigger={(open) => (
          <span className="flex cursor-pointer items-center gap-1.25 text-sm text-gray-900">
            {value || t('massEmail.fromPlaceholder')}
            <ChevronDownIcon
              className={cn(
                'size-4 text-gray-500 transition-transform duration-200',
                open && 'rotate-180',
              )}
            />
          </span>
        )}
      >
        {(close) => (
          <div className="flex min-w-56 flex-col gap-0.5 rounded-md bg-white p-1 shadow-popover">
            {fromEmails.length === 0 ? (
              <p className="px-2.5 py-1.5 text-sm text-gray-500">
                {t('massEmail.noEmails')}
              </p>
            ) : (
              fromEmails.map((address) => (
                <button
                  key={address}
                  type="button"
                  onClick={() => {
                    onChange(address)
                    close()
                  }}
                  className="flex h-8 cursor-pointer items-center rounded-sm px-2.5 text-left text-sm text-gray-900 hover:bg-gray-100"
                >
                  {address}
                </button>
              ))
            )}
          </div>
        )}
      </Popover>
    </div>
  )
}

function SubjectRow({
  value,
  onChange,
}: {
  value: string
  onChange: (value: string) => void
}) {
  const { t } = useTranslation()
  return (
    <div className="flex shrink-0 items-center gap-2.5 border-b border-gray-200 bg-gray-50 px-2.5 py-2">
      <span className="text-sm text-gray-500 select-none">
        {t('massEmail.subjectLabel')}
      </span>
      <Input
        inputSize="sm"
        clearable
        value={value}
        onChange={(event) => onChange(event.target.value)}
        placeholder={t('massEmail.subjectPlaceholder')}
        className="min-w-0 flex-1"
      />
    </div>
  )
}

function Footer({
  willReceive,
  hasMessage,
  isSending,
  onCancel,
  onSend,
}: {
  willReceive: number
  hasMessage: boolean
  isSending?: boolean
  onCancel: () => void
  onSend: () => void
}) {
  const { t } = useTranslation()
  return (
    <div className="flex h-14 shrink-0 items-center justify-between gap-2.5 border-t border-gray-200 py-2.5 pr-2.5 pl-5">
      <div className="flex items-center gap-1.75">
        <span className="size-1.75 shrink-0 rounded-full bg-green-500" />
        <p className="text-sm text-gray-500">
          {t('massEmail.footer.summary', { willReceive })}
        </p>
      </div>
      <div className="flex items-center gap-2.5">
        <Button
          type="button"
          variant="outline"
          size="xs"
          pressEffect
          className="px-3.5"
          onClick={onCancel}
        >
          {t('massEmail.cancel')}
        </Button>
        <Button
          type="button"
          size="xs"
          pressEffect
          className="gap-2.5 px-3.75"
          disabled={!hasMessage || isSending}
          onClick={onSend}
        >
          <SendIcon className="size-3.5" />
          {hasMessage
            ? t('massEmail.send', { count: willReceive })
            : t('massEmail.sendEmpty')}
        </Button>
      </div>
    </div>
  )
}

/** Loaded templates are designed HTML — keep images, links, styles and div
 *  layout through the editor round-trip. */
function MessageEditor({
  email,
  slashGroups,
}: {
  email: ReturnType<typeof useMassEmail>
  slashGroups: SlashCommandGroup[]
}) {
  const { t } = useTranslation()
  return (
    <NoteEditor
      value={email.message}
      onChange={email.setMessage}
      controllerRef={email.editorRef}
      minHeight={250}
      placeholder={t('massEmail.messagePlaceholder')}
      slashGroups={slashGroups}
      enableVoice
      enableTable
      enableImage
      enableLink
      enableHtmlFidelity
    />
  )
}

function MassEmailDialogBody({
  onOpenChange,
  onSent,
  viewModel,
  drafts,
}: Omit<MassEmailDialogProps, 'open' | 'perTabDraft'> & {
  drafts: { message: DraftState; subject: DraftState }
}) {
  const { t } = useTranslation()
  const email = useMassEmail(viewModel, drafts)
  const { options: templateOptions, isLoading: templateLoading } =
    useTemplateOptions({ templateType: TEMPLATE_TYPE.email })
  const { groups: insertFieldGroups } = useTemplateVariableOptions()
  const slashGroups = useMemo(
    () => insertFieldsToSlashGroups(insertFieldGroups),
    [insertFieldGroups],
  )
  const loadTemplate = useLoadTemplate()
  const handleSend = async () => {
    if (await email.send()) {
      onSent?.()
      onOpenChange(false)
    }
  }
  return (
    <DialogContent className="max-w-xl">
      <Header />
      <div className="flex min-h-0 flex-1 flex-col overflow-y-auto">
        <FromRow
          fromEmails={viewModel.fromEmails}
          value={email.fromEmail}
          onChange={email.setFromEmail}
        />
        <MassRecipients
          totalSelected={email.totalSelected}
          willReceive={email.willReceive}
          removed={email.removed}
          expanded={email.expandedRemoved}
          onToggle={email.toggleRemoved}
          labels={buildMassRecipientsLabels(t, 'massEmail', {
            totalSelected: email.totalSelected,
            willReceive: email.willReceive,
            removedCount: email.removedCount,
          })}
        />
        <TemplateComposeToolbar
          templateOptions={templateOptions}
          templateLoading={templateLoading}
          onTemplateSelect={(id) =>
            void loadTemplate(id).then((template) => {
              email.setMessage(
                relabelTemplateBody(template.body, insertFieldGroups),
              )
              if (template.subject) email.setSubject(template.subject)
            })
          }
        />
        <SubjectRow value={email.subject} onChange={email.setSubject} />
        <MessageEditor email={email} slashGroups={slashGroups} />
      </div>
      <div className="shrink-0 border-t border-gray-200 px-5 py-2.5">
        <p className="text-xs text-gray-500">
          {t('massEmail.note', { count: email.willReceive })}
        </p>
      </div>
      <Footer
        willReceive={email.willReceive}
        hasMessage={email.hasMessage}
        isSending={email.isSending}
        onCancel={() => onOpenChange(false)}
        onSend={() => void handleSend()}
      />
    </DialogContent>
  )
}

export function MassEmailDialog(props: MassEmailDialogProps) {
  const perTabMessage = useWorkspaceTabState('massEmailBody', '')
  const perTabSubject = useWorkspaceTabState('massEmailSubject', '')
  const localMessage = useState('')
  const localSubject = useState('')
  const perTab = props.perTabDraft !== false
  const drafts = {
    message: perTab ? perTabMessage : localMessage,
    subject: perTab ? perTabSubject : localSubject,
  }
  const onOpenChange = (open: boolean) => {
    // An explicit close discards the drafts. A workspace-tab switch never
    // goes through here — the other tab simply reads its own per-tab state.
    if (!open) {
      drafts.message[1]('')
      drafts.subject[1]('')
    }
    props.onOpenChange(open)
  }
  return (
    <Dialog open={props.open} onOpenChange={onOpenChange}>
      {props.open && (
        <MassEmailDialogBody
          {...props}
          onOpenChange={onOpenChange}
          drafts={drafts}
        />
      )}
    </Dialog>
  )
}
